/**
 * First-touch attribution capture.
 *
 * Records UTM params, the referring site, and the first page a visitor landed
 * on, once per browser session. Lead submissions attach this automatically so
 * we can tell which channels actually produce inquiries and applications.
 */

const KEY = "kcc_attribution";

export type Attribution = {
  utm_source?: string;
  utm_medium?: string;
  utm_campaign?: string;
  landing_page?: string;
  referrer?: string;
};

const clean = (value: string | null | undefined, max = 300) =>
  value ? value.trim().slice(0, max) : undefined;

export const captureAttribution = () => {
  if (typeof window === "undefined") return;
  try {
    if (sessionStorage.getItem(KEY)) return;
    const params = new URLSearchParams(window.location.search);
    const ref = document.referrer;
    const sameSite = ref ? new URL(ref).hostname === window.location.hostname : true;

    const data: Attribution = {
      utm_source: clean(params.get("utm_source"), 100),
      utm_medium: clean(params.get("utm_medium"), 100),
      utm_campaign: clean(params.get("utm_campaign"), 100),
      landing_page: clean(window.location.pathname + window.location.search),
      referrer: sameSite ? undefined : clean(ref),
    };
    sessionStorage.setItem(KEY, JSON.stringify(data));
  } catch {
    /* storage unavailable — attribution is best-effort */
  }
};

export const getAttribution = (): Attribution => {
  if (typeof window === "undefined") return {};
  try {
    return JSON.parse(sessionStorage.getItem(KEY) || "{}") as Attribution;
  } catch {
    return {};
  }
};

/** Options shown in the "How did you hear about us?" field. */
export const LEAD_SOURCE_OPTIONS = [
  "Google or another search engine",
  "Referred by a friend or family member",
  "Referred by a healthcare or senior-living professional",
  "Social media",
  "Indeed or another job board",
  "School, university, or nursing program",
  "Community event or church",
  "Other",
] as const;
